import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { DischargeFormData } from '../../types/formData'
import { DischargeFormField } from '../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../helpers'

/**
 * Discharge Summary subsection adapter
 * Handles all discharge summary fields (11 fields)
 */
export class DischargeSummaryAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions using existing helpers
    const typesOfServices = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_TYPES_OF_SERVICES
    )

    const serviceFrequency = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_SERVICE_FREQUENCY
    )?.at(0)

    const lastDateOfService = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_LAST_DATE_OF_SERVICE
    )?.at(0)

    const dischargeCondition = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_DISCHARGE_CONDITION
    )?.at(0)

    const reasonForDischarge = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_REASON_FOR_DISCHARGE
    )?.at(0)

    const laboratoryData = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_LABORATORY_DATA
    )?.at(0)

    const planOfCareOutcomes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_PLAN_OF_CARE_OUTCOMES
    )?.at(0)

    const dischargeInstructions = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_DISCHARGE_INSTRUCTIONS
    )?.at(0)

    const postDischargeTreatment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_POST_DISCHARGE_TREATMENT
    )

    const postDischargeGoals = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_POST_DISCHARGE_GOALS
    )

    return {
      [DischargeFormField.TYPES_OF_SERVICES]: typesOfServices,
      [DischargeFormField.SERVICE_FREQUENCY]: serviceFrequency,
      [DischargeFormField.LAST_DATE_OF_SERVICE]: lastDateOfService,
      [DischargeFormField.DISCHARGE_CONDITION]: dischargeCondition,
      [DischargeFormField.REASON_FOR_DISCHARGE]: reasonForDischarge,
      [DischargeFormField.LABORATORY_DATA]: laboratoryData,
      [DischargeFormField.PLAN_OF_CARE_OUTCOMES]: planOfCareOutcomes,
      [DischargeFormField.DISCHARGE_INSTRUCTIONS]: dischargeInstructions,
      [DischargeFormField.POST_DISCHARGE_TREATMENT]: postDischargeTreatment,
      [DischargeFormField.POST_DISCHARGE_GOALS]: postDischargeGoals
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.TYPES_OF_SERVICES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_TYPES_OF_SERVICES,
        value: this.getFormDataField(DischargeFormField.TYPES_OF_SERVICES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.SERVICE_FREQUENCY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_SERVICE_FREQUENCY,
        value: this.getFormDataField(DischargeFormField.SERVICE_FREQUENCY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.LAST_DATE_OF_SERVICE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_LAST_DATE_OF_SERVICE,
        value: this.getFormDataField(DischargeFormField.LAST_DATE_OF_SERVICE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.DISCHARGE_CONDITION)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_DISCHARGE_CONDITION,
        value: this.getFormDataField(DischargeFormField.DISCHARGE_CONDITION)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.REASON_FOR_DISCHARGE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_REASON_FOR_DISCHARGE,
        value: this.getFormDataField(DischargeFormField.REASON_FOR_DISCHARGE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.LABORATORY_DATA)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_LABORATORY_DATA,
        value: this.getFormDataField(DischargeFormField.LABORATORY_DATA)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PLAN_OF_CARE_OUTCOMES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_PLAN_OF_CARE_OUTCOMES,
        value: this.getFormDataField(DischargeFormField.PLAN_OF_CARE_OUTCOMES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.DISCHARGE_INSTRUCTIONS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_DISCHARGE_INSTRUCTIONS,
        value: this.getFormDataField(DischargeFormField.DISCHARGE_INSTRUCTIONS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.POST_DISCHARGE_TREATMENT)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_POST_DISCHARGE_TREATMENT,
        value: this.getFormDataField(DischargeFormField.POST_DISCHARGE_TREATMENT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.POST_DISCHARGE_GOALS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.DISCHARGE_SUMMARY_POST_DISCHARGE_GOALS,
        value: this.getFormDataField(DischargeFormField.POST_DISCHARGE_GOALS)
      })
    }

    return mutations
  }
}
